import { useMemo } from 'react';
import {
  Building2, Users, FileCheck, TrendingUp, ArrowUpRight, ArrowDownRight,
  Headphones, UserCheck, AlertTriangle, Clock, Shield, Activity,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area,
} from 'recharts';
import { Link } from 'react-router';
import { useTenantContext, PLAN_CONFIG, STATUS_CONFIG } from '../context/TenantContext';
import { useSimulaData } from '../hooks/useSimulaData';
import { isConcludedAuditStatus } from '../utils/auditStatus';

// ── Helpers ───────────────────────────────────────────────────────────
const PLAN_COLORS: Record<string, string> = {
  starter: 'var(--chart-3)',
  professional: 'var(--chart-4)',
  enterprise: 'var(--primary)',
};

const tooltipStyle: React.CSSProperties = {
  backgroundColor: 'var(--card)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  fontFamily: 'var(--font-family-inter)',
  fontSize: 'var(--text-xs)',
};

function toYearMonth(value: unknown): string | null {
  if (!value) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return value.toISOString().slice(0, 7);
  }
  if (typeof value === 'number') {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toISOString().slice(0, 7);
  }
  if (typeof value === 'string') {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toISOString().slice(0, 7);
  }
  return null;
}

/**
 * Builds volume history for the last 6 months using real tenant creation dates
 * and real audit/training counts.
 */
function buildVolumeHistory(
  tenants: { createdAt: unknown; currentUsers: number; status: string }[],
  auditsByMonth: Record<string, number>,
) {
  const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  const now = new Date();

  // Generate last 6 months dynamically
  const months: { label: string; key: string }[] = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      label: monthNames[d.getMonth()],
      key: d.toISOString().slice(0, 7),
    });
  }

  return months.map(({ label, key }) => {
    const activeTenants = tenants.filter(t => {
      const createdAtKey = toYearMonth(t.createdAt);
      return createdAtKey ? createdAtKey <= key && t.status !== 'suspended' : false;
    });
    const empresas = activeTenants.length;
    const usuarios = activeTenants.reduce((s, t) => s + t.currentUsers, 0);
    const auditorias = auditsByMonth[key] || 0;

    return { month: label, empresas, usuarios, auditorias };
  });
}

// ── Component ─────────────────────────────────────────────────────────
export function SuperAdminDashboard() {
  const { tenants, adminUsers, activityLogs } = useTenantContext();
  const {
    audits: auditsEntity,
    trainingSessions: trainingsEntity,
  } = useSimulaData();
  const allAudits = auditsEntity.data;
  const allTrainings = trainingsEntity.data;

  // Contagem de auditorias por mês (YYYY-MM)
  const auditCountsByMonth = useMemo(() => {
    const counts: Record<string, number> = {};
    allAudits.filter(a => isConcludedAuditStatus(a.status)).forEach(a => {
      const key = toYearMonth(a.date);
      if (key) counts[key] = (counts[key] || 0) + 1;
    });
    allTrainings.filter(t => t.evaluation).forEach(t => {
      const key = toYearMonth(t.date);
      if (key) counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [allAudits, allTrainings]);

  // ── Derived metrics ────────────────────────────────────────────────
  const metrics = useMemo(() => {
    const totalUsers = tenants.reduce((s, t) => s + t.currentUsers, 0);
    const totalOperators = tenants.reduce((s, t) => s + t.currentOperators, 0);
    const activeCompanies = tenants.filter(t => t.status === 'active').length;
    const trialCompanies = tenants.filter(t => t.status === 'trial').length;
    const suspendedCompanies = tenants.filter(t => t.status === 'suspended').length;

    // Score médio real a partir das auditorias com avaliação
    const evaluatedAudits = allAudits.filter(a => isConcludedAuditStatus(a.status) && a.evaluation);
    const avgGlobalScore = evaluatedAudits.length > 0
      ? evaluatedAudits.reduce((s, a) => s + (a.evaluation?.score || 0), 0) / evaluatedAudits.length
      : 0;

    // Contagem real de auditorias do mês atual
    const now = new Date();
    const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const totalAuditsMonth = auditCountsByMonth[currentMonthKey] || 0;

    return {
      totalUsers, totalOperators, totalAuditsMonth,
      activeCompanies, trialCompanies, suspendedCompanies,
      avgGlobalScore,
      totalAdmins: adminUsers.length,
      recentLogsCount: activityLogs.filter(l => {
        const logDate = new Date(l.performedAt);
        const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        return logDate >= weekAgo;
      }).length,
    };
  }, [tenants, adminUsers, activityLogs, allAudits, auditCountsByMonth]);

  // ── Chart data ─────────────────────────────────────────────────────
  const volumeByMonth = useMemo(() => buildVolumeHistory(tenants, auditCountsByMonth), [tenants, auditCountsByMonth]);

  const planDistribution = useMemo(() => {
    const counts: Record<string, number> = { starter: 0, professional: 0, enterprise: 0 };
    tenants.forEach(t => { counts[t.plan] = (counts[t.plan] || 0) + 1; });
    return Object.entries(counts)
      .filter(([, v]) => v > 0)
      .map(([plan, value]) => ({
        name: PLAN_CONFIG[plan as keyof typeof PLAN_CONFIG]?.label || plan,
        value,
        color: PLAN_COLORS[plan] || 'var(--muted-foreground)',
      }));
  }, [tenants]);

  const operatorsByCompany = useMemo(() =>
    tenants
      .filter(t => t.currentOperators > 0)
      .sort((a, b) => b.currentOperators - a.currentOperators)
      .map(t => ({
        name: t.name.length > 14 ? t.name.slice(0, 14) + '...' : t.name,
        operadores: t.currentOperators,
      })),
    [tenants]
  );

  // ── Dynamic alerts ─────────────────────────────────────────────────
  const alerts = useMemo(() => {
    const items: { icon: typeof AlertTriangle; color: string; title: string; detail: string }[] = [];

    // Suspended tenants
    tenants
      .filter(t => t.status === 'suspended')
      .forEach(t => {
        items.push({
          icon: AlertTriangle,
          color: 'var(--error)',
          title: `${t.name} suspensa`,
          detail: `Inativa desde ${new Date(t.lastActivityAt).toLocaleDateString('pt-BR')}`,
        });
      });

    // Trials expiring soon
    tenants
      .filter(t => t.status === 'trial' && t.trialEndsAt)
      .forEach(t => {
        const daysLeft = Math.ceil(
          (new Date(t.trialEndsAt!).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
        );
        if (daysLeft <= 30) {
          items.push({
            icon: Clock,
            color: 'var(--warning)',
            title: t.name,
            detail: `Trial expira em ${new Date(t.trialEndsAt!).toLocaleDateString('pt-BR')} (${daysLeft}d)`,
          });
        }
      });

    // High usage tenants (>60% of max users)
    tenants
      .filter(t => t.status === 'active' && t.currentUsers / t.maxUsers > 0.6)
      .forEach(t => {
        const pct = Math.round((t.currentUsers / t.maxUsers) * 100);
        items.push({
          icon: UserCheck,
          color: 'var(--chart-4)',
          title: `${t.name} — uso alto`,
          detail: `${t.currentUsers}/${t.maxUsers} usuarios (${pct}%)`,
        });
      });

    // Active tenants with high operator volume
    tenants
      .filter(t => t.status === 'active' && t.currentOperators > 50)
      .forEach(t => {
        items.push({
          icon: Headphones,
          color: 'var(--secondary)',
          title: `${t.name} — volume crescente`,
          detail: `${t.currentOperators} operadores ativos`,
        });
      });

    return items.slice(0, 5); // max 5 alerts
  }, [tenants]);

  // ── KPI Cards config ───────────────────────────────────────────────
  const kpis = [
    {
      label: 'Empresas',
      value: tenants.length,
      sub: `${metrics.activeCompanies} ativas · ${metrics.trialCompanies} trial${metrics.suspendedCompanies ? ` · ${metrics.suspendedCompanies} susp.` : ''}`,
      icon: Building2,
      color: 'var(--primary)',
      delta: `${metrics.trialCompanies} em trial`,
      up: metrics.activeCompanies > 0,
    },
    {
      label: 'Usuarios Totais',
      value: metrics.totalUsers.toLocaleString('pt-BR'),
      sub: `${metrics.totalOperators} operadores`,
      icon: Users,
      color: 'var(--chart-4)',
      delta: `${metrics.totalAdmins} admins`,
      up: true,
    },
    {
      label: 'Auditorias / Mes',
      value: metrics.totalAuditsMonth.toLocaleString('pt-BR'),
      sub: 'Auditorias + treinos neste mês',
      icon: FileCheck,
      color: 'var(--secondary)',
      delta: `${Object.values(auditCountsByMonth).reduce((s, v) => s + v, 0)} total`,
      up: true,
    },
    {
      label: 'Score Medio Global',
      value: `${metrics.avgGlobalScore.toFixed(1)}%`,
      sub: `${tenants.filter(t => t.currentUsers > 0).length} empresas ativas`,
      icon: TrendingUp,
      color: 'var(--success)',
      delta: `${metrics.recentLogsCount} acoes na semana`,
      up: true,
    },
  ];

  return (
    <div className="space-y-6" style={{ fontFamily: 'var(--font-family-inter)' }}>
      {/* ── Header ──────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1
            className="text-foreground leading-snug"
            style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-weight-semibold)' }}
          >
            Visao Geral da Plataforma
          </h1>
          <p
            className="text-muted-foreground mt-1"
            style={{ fontSize: 'var(--text-sm)' }}
          >
            Dados em tempo real de {tenants.length} empresa{tenants.length !== 1 ? 's' : ''} cadastrada{tenants.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Link
          to="/superadmin/empresas"
          className="inline-flex items-center gap-2 transition-opacity hover:opacity-90 no-underline"
          style={{
            padding: '10px 20px',
            borderRadius: 'var(--radius-button)',
            backgroundColor: 'var(--primary)',
            color: 'var(--primary-foreground)',
            fontWeight: 'var(--font-weight-semibold)',
            fontSize: 'var(--text-sm)',
          }}
        >
          <Building2 size={16} /> Gerenciar Empresas
        </Link>
      </div>

      {/* ── KPI Cards ───────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((kpi) => (
          <div
            key={kpi.label}
            className="bg-card border border-border p-6"
            style={{ borderRadius: 'var(--radius-card)' }}
          >
            <div className="flex items-start justify-between mb-3">
              <div
                className="flex items-center justify-center"
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: 'var(--radius)',
                  backgroundColor: `color-mix(in srgb, ${kpi.color} 12%, transparent)`,
                  color: kpi.color,
                }}
              >
                <kpi.icon size={20} />
              </div>
              <div className="flex items-center gap-1">
                {kpi.up
                  ? <ArrowUpRight size={14} className="text-success" />
                  : <ArrowDownRight size={14} className="text-error" />}
                <span
                  className={kpi.up ? 'text-success' : 'text-error'}
                  style={{ fontSize: 'var(--text-xs)' }}
                >
                  {kpi.delta}
                </span>
              </div>
            </div>
            <p
              className="text-foreground"
              style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-weight-semibold)', lineHeight: 1.2 }}
            >
              {kpi.value}
            </p>
            <p className="text-muted-foreground mt-0.5" style={{ fontSize: 'var(--text-xs)' }}>
              {kpi.label}
            </p>
            <p className="text-muted-foreground mt-1.5 opacity-80" style={{ fontSize: 'var(--text-xs)' }}>
              {kpi.sub}
            </p>
          </div>
        ))}
      </div>

      {/* ── Charts Row ──────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Volume Evolution (area) */}
        <div
          className="lg:col-span-2 bg-card border border-border p-6"
          style={{ borderRadius: 'var(--radius-card)' }}
        >
          <div className="flex items-center justify-between mb-4">
            <h3
              className="text-foreground"
              style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--text-md)' }}
            >
              Evolucao de Volumes
            </h3>
            <span className="text-muted-foreground" style={{ fontSize: 'var(--text-xs)' }}>
              Ultimos 6 meses
            </span>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={volumeByMonth}>
              <defs>
                <linearGradient id="gradUsers" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--chart-4)" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="var(--chart-4)" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradAudits" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--secondary)" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="var(--secondary)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis
                dataKey="month"
                stroke="var(--muted-foreground)"
                fontSize={12}
                fontFamily="var(--font-family-inter)"
              />
              <YAxis
                stroke="var(--muted-foreground)"
                fontSize={12}
                fontFamily="var(--font-family-inter)"
              />
              <Tooltip contentStyle={tooltipStyle} />
              <Area type="monotone" dataKey="usuarios" stroke="var(--chart-4)" strokeWidth={2} fill="url(#gradUsers)" name="Usuarios" />
              <Area type="monotone" dataKey="auditorias" stroke="var(--secondary)" strokeWidth={2} fill="url(#gradAudits)" name="Auditorias" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Plan Distribution (donut) */}
        <div
          className="bg-card border border-border p-6"
          style={{ borderRadius: 'var(--radius-card)' }}
        >
          <h3
            className="text-foreground mb-4"
            style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--text-md)' }}
          >
            Empresas por Plano
          </h3>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie
                data={planDistribution}
                cx="50%"
                cy="50%"
                innerRadius={48}
                outerRadius={72}
                paddingAngle={4}
                dataKey="value"
              >
                {planDistribution.map((entry, i) => (
                  <Cell key={`plan-${i}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-4 mt-2">
            {planDistribution.map((p) => (
              <div key={p.name} className="flex items-center gap-2">
                <div
                  className="rounded-full"
                  style={{ width: '8px', height: '8px', backgroundColor: p.color }}
                />
                <span className="text-muted-foreground" style={{ fontSize: 'var(--text-xs)' }}>
                  {p.name} ({p.value})
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Second Row: Audits by Company + Alerts ──────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Audits bar chart */}
        <div
          className="lg:col-span-2 bg-card border border-border p-6"
          style={{ borderRadius: 'var(--radius-card)' }}
        >
          <h3
            className="text-foreground mb-4"
            style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--text-md)' }}
          >
            Operadores por Empresa
          </h3>
          {operatorsByCompany.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={operatorsByCompany} layout="vertical" margin={{ left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                <XAxis
                  type="number"
                  stroke="var(--muted-foreground)"
                  fontSize={12}
                  fontFamily="var(--font-family-inter)"
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={120}
                  stroke="var(--muted-foreground)"
                  fontSize={12}
                  fontFamily="var(--font-family-inter)"
                />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="operadores" fill="var(--primary)" radius={[0, 4, 4, 0]} name="Operadores" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[220px] text-muted-foreground" style={{ fontSize: 'var(--text-sm)' }}>
              <Activity size={20} className="mr-2 opacity-50" />
              Nenhuma empresa com operadores ativos
            </div>
          )}
        </div>

        {/* Quick alerts / attention items */}
        <div
          className="bg-card border border-border p-6"
          style={{ borderRadius: 'var(--radius-card)' }}
        >
          <h3
            className="text-foreground mb-4"
            style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--text-md)' }}
          >
            Pontos de Atencao
          </h3>
          <div className="space-y-3">
            {alerts.length > 0 ? alerts.map((item, i) => (
              <div
                key={`alert-${i}`}
                className="flex items-start gap-3"
                style={{
                  padding: '12px',
                  borderRadius: 'var(--radius)',
                  backgroundColor: `color-mix(in srgb, ${item.color} 6%, transparent)`,
                  border: `1px solid color-mix(in srgb, ${item.color} 15%, transparent)`,
                }}
              >
                <div
                  className="flex items-center justify-center shrink-0"
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: 'var(--radius)',
                    backgroundColor: `color-mix(in srgb, ${item.color} 14%, transparent)`,
                    color: item.color,
                  }}
                >
                  <item.icon size={16} />
                </div>
                <div>
                  <p
                    className="text-foreground"
                    style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}
                  >
                    {item.title}
                  </p>
                  <p className="text-muted-foreground mt-0.5" style={{ fontSize: 'var(--text-xs)' }}>
                    {item.detail}
                  </p>
                </div>
              </div>
            )) : (
              <div className="flex items-center gap-2 text-muted-foreground p-4" style={{ fontSize: 'var(--text-sm)' }}>
                <Shield size={18} className="opacity-50" />
                Nenhum ponto de atencao no momento
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Company Summary Table ───────────────────────────────── */}
      <div
        className="bg-card border border-border p-6"
        style={{ borderRadius: 'var(--radius-card)' }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3
            className="text-foreground"
            style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--text-md)' }}
          >
            Resumo por Empresa
          </h3>
          <Link
            to="/superadmin/empresas"
            className="flex items-center gap-1 transition-opacity hover:opacity-80 text-primary no-underline"
            style={{ fontSize: 'var(--text-xs)' }}
          >
            Gerenciar <ArrowUpRight size={12} />
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr className="border-b border-border">
                {['Empresa', 'Plano', 'Status', 'Usuarios', 'Operadores', 'Admins', 'Ult. Atividade'].map((h) => (
                  <th
                    key={h}
                    className="text-left text-muted-foreground whitespace-nowrap"
                    style={{
                      padding: '10px 14px',
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'var(--font-weight-semibold)',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tenants.map((t) => {
                const planColor = PLAN_COLORS[t.plan] || 'var(--muted-foreground)';
                const st = STATUS_CONFIG[t.status] || STATUS_CONFIG.inactive;
                const tenantAdmins = adminUsers.filter(a => a.tenantId === t.id);
                const usagePercent = Math.round((t.currentUsers / t.maxUsers) * 100);
                return (
                  <tr key={t.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                    <td
                      className="text-foreground"
                      style={{
                        padding: '14px',
                        fontWeight: 'var(--font-weight-medium)',
                        fontSize: 'var(--text-sm)',
                      }}
                    >
                      <div className="flex flex-col">
                        <span>{t.name}</span>
                        <span className="text-muted-foreground" style={{ fontSize: 'var(--text-xs)' }}>
                          {t.domain}
                        </span>
                      </div>
                    </td>
                    <td style={{ padding: '14px' }}>
                      <span
                        style={{
                          padding: '2px 10px',
                          borderRadius: '9999px',
                          fontSize: 'var(--text-xs)',
                          fontWeight: 'var(--font-weight-medium)',
                          backgroundColor: `color-mix(in srgb, ${planColor} 12%, transparent)`,
                          color: planColor,
                        }}
                      >
                        {PLAN_CONFIG[t.plan]?.label || t.plan}
                      </span>
                    </td>
                    <td style={{ padding: '14px' }}>
                      <span
                        style={{
                          padding: '2px 10px',
                          borderRadius: '9999px',
                          fontSize: 'var(--text-xs)',
                          fontWeight: 'var(--font-weight-medium)',
                          backgroundColor: st.bg,
                          color: st.color,
                        }}
                      >
                        {st.label}
                      </span>
                    </td>
                    <td style={{ padding: '14px' }}>
                      <div className="flex flex-col">
                        <span
                          className="text-foreground"
                          style={{ fontSize: 'var(--text-sm)', fontFamily: 'var(--font-family-mono)' }}
                        >
                          {t.currentUsers}/{t.maxUsers}
                        </span>
                        {/* Usage bar */}
                        <div
                          className="mt-1"
                          style={{
                            width: '60px',
                            height: '4px',
                            borderRadius: '2px',
                            backgroundColor: 'var(--muted)',
                            overflow: 'hidden',
                          }}
                        >
                          <div
                            style={{
                              width: `${Math.min(usagePercent, 100)}%`,
                              height: '100%',
                              borderRadius: '2px',
                              backgroundColor: usagePercent > 80 ? 'var(--error)' : usagePercent > 60 ? 'var(--warning)' : 'var(--success)',
                              transition: 'width 0.3s ease',
                            }}
                          />
                        </div>
                      </div>
                    </td>
                    <td
                      className="text-foreground"
                      style={{ padding: '14px', fontSize: 'var(--text-sm)', fontFamily: 'var(--font-family-mono)' }}
                    >
                      {t.currentOperators > 0 ? t.currentOperators.toLocaleString('pt-BR') : '-'}
                    </td>
                    <td style={{ padding: '14px' }}>
                      <div className="flex items-center gap-1">
                        <span
                          className="text-foreground"
                          style={{ fontSize: 'var(--text-sm)', fontFamily: 'var(--font-family-mono)' }}
                        >
                          {tenantAdmins.length}
                        </span>
                        {tenantAdmins.some(a => a.status === 'inactive') && (
                          <span
                            className="text-warning"
                            style={{ fontSize: 'var(--text-xs)' }}
                            title="Tem admins inativos"
                          >
                            !
                          </span>
                        )}
                      </div>
                    </td>
                    <td
                      className="text-muted-foreground"
                      style={{ padding: '14px', fontSize: 'var(--text-xs)' }}
                    >
                      {new Date(t.lastActivityAt).toLocaleDateString('pt-BR')}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {tenants.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground gap-3">
            <Building2 size={40} className="opacity-30" />
            <p style={{ fontSize: 'var(--text-sm)' }}>Nenhuma empresa cadastrada</p>
            <Link
              to="/superadmin/empresas"
              className="text-primary no-underline hover:opacity-80 transition-opacity"
              style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}
            >
              Cadastrar primeira empresa
            </Link>
          </div>
        )}
      </div>

      {/* ── Recent Activity Log (mini) ─────────────────────────── */}
      <div
        className="bg-card border border-border p-6"
        style={{ borderRadius: 'var(--radius-card)' }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3
            className="text-foreground"
            style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--text-md)' }}
          >
            Atividades Recentes
          </h3>
          <span className="text-muted-foreground" style={{ fontSize: 'var(--text-xs)' }}>
            {activityLogs.length} registro{activityLogs.length !== 1 ? 's' : ''} total
          </span>
        </div>
        <div className="space-y-2">
          {activityLogs.slice(0, 6).map((log) => (
            <div
              key={log.id}
              className="flex items-center gap-3 p-3 hover:bg-muted/30 transition-colors"
              style={{ borderRadius: 'var(--radius)' }}
            >
              <div
                className="flex items-center justify-center shrink-0"
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: 'var(--radius)',
                  backgroundColor: log.entityType === 'tenant'
                    ? 'color-mix(in srgb, var(--primary) 10%, transparent)'
                    : 'color-mix(in srgb, var(--secondary) 10%, transparent)',
                  color: log.entityType === 'tenant' ? 'var(--primary)' : 'var(--secondary)',
                }}
              >
                {log.entityType === 'tenant' ? <Building2 size={14} /> : <Users size={14} />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-foreground truncate" style={{ fontSize: 'var(--text-sm)' }}>
                  <span style={{ fontWeight: 'var(--font-weight-medium)' }}>{log.entityName}</span>
                  <span className="text-muted-foreground"> — {log.details}</span>
                </p>
              </div>
              <span
                className="text-muted-foreground shrink-0"
                style={{ fontSize: 'var(--text-xs)', fontFamily: 'var(--font-family-mono)' }}
              >
                {new Date(log.performedAt).toLocaleDateString('pt-BR')}
              </span>
            </div>
          ))}
        </div>
        {activityLogs.length === 0 && (
          <p className="text-muted-foreground text-center py-6" style={{ fontSize: 'var(--text-sm)' }}>
            Nenhuma atividade registrada
          </p>
        )}
      </div>
    </div>
  );
}

export default SuperAdminDashboard;
